import { useTranslation } from 'next-i18next';
import translationFallback from '@/utilites/translations/translationFallback';

interface AuthenticatingStructuredDataProps {
  canonicalUrl?: string;
}

export default function AuthenticatingStructuredData({
                                                       canonicalUrl = 'https://spiritory.com/authenticating'
                                                     }: AuthenticatingStructuredDataProps) {
  const { t } = useTranslation('authenticating');
  const { t: tCommon } = useTranslation('common');

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": canonicalUrl,
        "url": canonicalUrl,
        "name": translationFallback(
          'authenticating_title',
          tCommon('authenticating_title'),
          'Authenticating Whisky Bottles – Spiritory'
        ),
        "description": translationFallback(
          'authenticating_description',
          tCommon('authenticating_description'),
          'Learn how Spiritory digitally authenticates whisky bottles to guarantee trust and quality in every transaction.'
        )
      },
      {
        "@type": "HowTo",
        "name": translationFallback(
          'authenticationGuideTitle',
          t('authenticationGuideTitle'),
          'How to Authenticate Whisky Bottles on Spiritory'
        ),
        "description": "Step-by-step guide on the digital authentication process for whisky bottles on Spiritory.",
        "image": "https://spiritory.com/images/authenticating/authenticating2.webp",
        "totalTime": "PT10M",
        "step": [
          {
            "@type": "HowToStep",
            "name": "Upload Bottle Details",
            "text": translationFallback(
              'submitting_your_images_text',
              t('submitting_your_images_text'),
              'Sellers upload detailed photos and information about the bottle.'
            )
          },
          {
            "@type": "HowToStep",
            "name": "Digital Verification",
            "text": translationFallback(
              'authenticationText',
              t('authenticationText'),
              "Spiritory's authentication experts review the details and verify the bottle's authenticity."
            )
          },
          {
            "@type": "HowToStep",
            "name": "Secure Listing",
            "text": "Once authenticated, the bottle is securely listed for buyers to browse."
          },
          {
            "@type": "HowToStep",
            "name": "Buyer Confidence",
            "text": translationFallback(
              'authenticationText2',
              t('authenticationText2'),
              'Buyers can trust the digital authentication badge when making a purchase.'
            )
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": translationFallback(
              'faqs.faq1_q',
              t('faqs.faq1_q'),
              'How does the product authentication works?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqs.faq1_a',
                t('faqs.faq1_a'),
                'Sellers upload detailed images of the bottle and packaging for review. Our expert team verifies authenticity and provides feedback within 48 hours. Only authenticated products are approved for sale.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqQuestion1',
              t('faqQuestion1'),
              'What happens, if the authentication fails?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqAnswer1',
                t('faqAnswer1'),
                "In that case, you'll get a rejection notice for the transaction. If the authentication fails, the bottle does not meet all the conditions of the bottle state, which you can learn more about here."
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqQuestion2',
              t('faqQuestion2'),
              'How long does the authentication take?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqAnswer2',
                t('faqAnswer2'),
                'Authentication usually takes from 1 hour to 48 hours.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqQuestion3',
              t('faqQuestion3'),
              'How much time does the seller have to provide the photos?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqAnswer3',
                t('faqAnswer3'),
                'Seller has 48 hours to provide a photo.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqs.faq2_q',
              t('faqs.faq2_q'),
              'Can I verify a products authenticity before purchasing it?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqs.faq2_a',
                t('faqs.faq2_a'),
                'Once you place your order, the product authentication process begins and can take from 1 to 48 hours. Once the authentication is approved by our team, you can make the payment. This means that you will not have to pay for the product before verifying it with our team of experts'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqs.faq3_q',
              t('faqs.faq3_q'),
              'Does Spiritory perform quality checks on the products?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqs.faq3_a',
                t('faqs.faq3_a'),
                'Yes, all products undergo rigorous checks to ensure they meet Spiritory\'s quality standards, including unopened seals, intact labels, and original packaging.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqs.faq4_q',
              t('faqs.faq4_q'),
              'Can I request additional information about a specific products authenticity?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqs.faq4_a',
                t('faqs.faq4_a'),
                'If you need information regarding our quality policies and authentication process you can find our authentication guide at the top of the page under About > Authentication.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqs.faq5_q',
              t('faqs.faq5_q'),
              'Can I see a picture of the bottle before purchase it?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqs.faq5_a',
                t('faqs.faq5_a'),
                'You cannot see a picture of the product before purchasing. Our team will ensure the quality and condition of the product through our authentication test.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqs.faq6_q',
              t('faqs.faq6_q'),
              'What happens if the bottle fails authentication?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqs.faq6_a',
                t('faqs.faq6_a'),
                'If a bottle fails authentication, the transaction is canceled, and the bottle is not released for sale. The seller may re-submit the product for review if verification materials were insufficient.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqs.faq7_q',
              t('faqs.faq7_q'),
              'How do I know that my bottle has been authenticated?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqs.faq7_a',
                t('faqs.faq7_a'),
                'You will receive a confirmation email once the bottle passes authentication, ensuring it meets Spiritory\'s standards.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faqs.faq8_q',
              t('faqs.faq8_q'),
              'Can I reject the authentication of my products?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faqs.faq8_a',
                t('faqs.faq8_a'),
                'Only approved commercial sellers with an established reputation may opt out of authentication. For private sellers, authentication is mandatory to ensure platform security and buyer trust.'
              )
            }
          }
        ]
      },
      {
        "@type": "Organization",
        "name": "Spiritory",
        "url": "https://spiritory.com",
        "logo": "https://spiritory.com/images/logo.png",
        "sameAs": [
          "https://www.facebook.com/spiritory",
          "https://twitter.com/spiritory",
          "https://www.instagram.com/spiritory"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+34-XXX-XXX-XXX",
          "contactType": "customer service",
          "areaServed": "ES",
          "availableLanguage": [
            "Spanish", "English", "French", "Italian", "German"
          ]
        }
      }
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }}
    />
  );
}